'use client';

import LLDropdown from '@next-app/components/elements/LLDropdown/LLDropdown';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import { FC, useEffect, useState } from 'react';
import urlMapping from '@next-app/config/urlMapping';
import { makeFetchCall } from '@next-app/utils/fetch-data';
import { useSelector } from 'react-redux';
import { selectCountries } from '@next-app/lib/features/checkout/CheckoutSelector';
import { isNotEmptyObject } from '@next-app/utils/common-utils';
import { isNotEmptyArray } from '@next-app/components/shared/ProductDetails/helper';
import Conditional from '@next-app/components/Conditional';
import { FIELD_ACCEPTED_CHARACTERS } from '@next-app/constants/CheckoutConstants';
import { selectIsFurniture } from '@next-app/lib/features/InitSelectors';

interface Option {
  label: string;
  value: string;
}

interface CountryStateInterface {
  code: string;
  displayName: string;
}

interface StateListProps {
  country: string;
  value: string;
  errorMsg?: string;
  name?: string;
  automationId?: string;
  updateHandler: Function;
  onBlurHandler: Function;
}

const StateList: FC<StateListProps> = ({
  updateHandler,
  country,
  name,
  value,
  automationId,
  errorMsg,
  onBlurHandler,
}) => {
  const [stateList, setStateList] = useState<Option[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const countryList = useSelector(selectCountries);
  const isFurniture = useSelector(selectIsFurniture);

  useEffect(() => {
    const fetchStates = async () => {
      if (!country) return;

      const selectedCountry = countryList?.find(
        (el) => el.shortName === country,
      );
      const checkoutStateList =
        selectedCountry && isNotEmptyObject(selectedCountry)
          ? selectedCountry?.states
          : [];

      if (isNotEmptyArray(checkoutStateList)) {
        const transformedStateList = checkoutStateList.reduce<Option[]>(
          (acc, { shortName, name, disabled }) => {
            if (!disabled) acc.push({ label: name, value: shortName });
            return acc;
          },
          [],
        );
        setStateList(transformedStateList);
        // If there is at least one state, select the first by default and update parent
        if (transformedStateList.length > 0) {
          setSelectedState(transformedStateList[0].value);
          if (updateHandler) {
            updateHandler({ state: transformedStateList[0].value });
          }
        } else {
          setSelectedState('');
          if (updateHandler) {
            updateHandler({ state: '' });
          }
        }
      } else {
        const { states } = await makeFetchCall({
          endpoint: urlMapping.countryStateUrl,
          params: {
            country: encodeURIComponent(country),
          },
          headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
        });

        const transformedStateList =
          states?.map(({ displayName }: CountryStateInterface) => ({
            label: displayName,
            value: displayName,
          })) || [];

        setStateList(transformedStateList);
        if (transformedStateList.length > 0) {
          setSelectedState(transformedStateList[0].value);
          if (updateHandler) {
            updateHandler({ state: transformedStateList[0].value });
          }
        } else {
          setSelectedState('');
          if (updateHandler) {
            updateHandler({ state: '' });
          }
        }
      }
    };
    fetchStates();
  }, [country]);

  useEffect(() => {
    if (value) {
      setSelectedState(value);
    }
  }, [value]);

  return (
    <>
      <Conditional if={stateList.length > 0}>
        <LLDropdown
          options={stateList}
          autoComplete="address-level1"
          labelText="State"
          name={name}
          value={selectedState}
          onChange={(e) => {
            setSelectedState(e.target.value);
            updateHandler({ state: e.target.value });
          }}
          errorMsg={errorMsg}
          onBlur={(e) => onBlurHandler(e)}
          automationId={automationId}
        />
      </Conditional>
      <Conditional if={stateList.length === 0}>
        <LLInput
          placeholder="State"
          value={selectedState}
          onChange={(e) => {
            const value = e.target.value.replace(
              FIELD_ACCEPTED_CHARACTERS.STATE,
              '',
            );
            setSelectedState(value);
            updateHandler({ state: value });
          }}
          errorMsg={errorMsg}
          onBlur={(e) => onBlurHandler(e)}
          maxLength={40}
          automationId={automationId}
        />
      </Conditional>
    </>
  );
};

export default StateList;
